import * as Popover from '@radix-ui/react-popover';
import { useMemo } from 'react';
import type { CustomField } from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import { Checkbox } from '@/components/ui/checkbox';
import type { RenderableColumn } from './kanban-status-config';
import { getColumnFieldTag } from './kanban-i18n';

// ============================================
// KANBAN SETTINGS PANEL (cog popover)
// ============================================
//
// t15 R3: combined settings popover that supersedes the two toolbar
// popovers (ColumnsVisibilityPopover + CardFieldsPopover) the R1
// toolbar shipped. The user wanted a single cog icon in the top-right
// of the board (matching the Calendar/Gantt pattern), so we collapse
// both pickers into one Radix popover with two sections.
//
// Mirrors the CalendarSettingsPanel shape: a controlled popover
// (no Radix Trigger child) because the toolbar button lives in the
// parent and owns the open state. The parent passes the columns +
// selectedFieldIds from the active view's config, plus change
// callbacks that PATCH the saved view (or apply local state when
// there's no view — same as the Calendar null-activeView path).

const RENDERABLE_FIELD_TYPES = new Set(['TEXT', 'NUMBER', 'SELECT', 'MULTI_SELECT', 'CHECKBOX', 'URL', 'EMAIL']);

export interface KanbanSettingsPanelProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    columns: RenderableColumn[];
    customFields: CustomField[];
    selectedFieldIds: string[];
    onToggleColumn: (columnId: string, visible: boolean) => void;
    onToggleCardField: (fieldId: string) => void;
    /** Anchor element the popover should position against (the toolbar's cog button). */
    anchorRef?: React.RefObject<HTMLElement>;
    /** Disable every toggle (e.g. read-only view). */
    disabled?: boolean;
}

export function KanbanSettingsPanel({
    open,
    onOpenChange,
    columns,
    customFields,
    selectedFieldIds,
    onToggleColumn,
    onToggleCardField,
    anchorRef,
    disabled,
}: KanbanSettingsPanelProps) {
    const { t } = useI18n();

    const renderableFields = useMemo(
        () => customFields.filter((f) => RENDERABLE_FIELD_TYPES.has(f.type)),
        [customFields],
    );

    return (
        <Popover.Root open={open} onOpenChange={onOpenChange}>
            {/* R4 (t20): use Popover.Anchor + virtualRef so the popover
                actually positions against the toolbar's cog button.
                Previously this component passed `anchor={anchorRef.current}`
                as a prop on Popover.Content, but Popper.Content reads its
                reference element from the PopperContext, not from a prop —
                so the popover opened with no anchor reference and floated
                off-screen (Floating UI fell back to default positioning),
                which is what made the cog "do nothing" in R3. virtualRef
                lets us point at an HTMLElement that lives outside the
                Popover.Root subtree without rendering an extra anchor
                element in the DOM. Mirror of the Calendar fix from t16. */}
            {anchorRef ? <Popover.Anchor virtualRef={anchorRef} /> : null}
            <Popover.Portal>
                <Popover.Content
                    align="end"
                    sideOffset={6}
                    className="z-50 w-80 rounded-md border border-border bg-surface p-4 shadow-lg space-y-4"
                    data-testid="kanban-settings-panel"
                >
                    <div>
                        <h4 className="text-sm font-medium text-foreground mb-1">
                            {t('kanban.settingsTitle')}
                        </h4>
                        <p className="text-xs text-foreground-muted">
                            {t('kanban.settingsHint')}
                        </p>
                    </div>

                    {/* Column visibility section */}
                    <div className="space-y-2">
                        <h5 className="text-xs font-medium text-foreground uppercase tracking-wide">
                            {t('kanban.columnVisibilitySection')}
                        </h5>
                        <p className="text-[11px] text-foreground-muted">
                            {t('kanban.columnsVisibilityHint')}
                        </p>
                        <div className="space-y-1 max-h-56 overflow-y-auto rounded-md border border-border/40 bg-surface-hover/20 p-1">
                            {columns.length === 0 && (
                                <p className="text-xs text-foreground-muted px-2 py-3">
                                    {t('kanban.noColumns')}
                                </p>
                            )}
                            {columns.map((c) => (
                                <label
                                    key={c.id}
                                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer select-none"
                                >
                                    <Checkbox
                                        checked={c.visible !== false}
                                        disabled={disabled}
                                        onCheckedChange={(checked) =>
                                            onToggleColumn(c.id, checked === true)
                                        }
                                    />
                                    <span
                                        className="h-2.5 w-2.5 rounded-full shrink-0"
                                        style={{ backgroundColor: c.color || '#6366f1' }}
                                    />
                                    <span className="text-sm truncate flex-1">{c.name}</span>
                                    <span className="text-[10px] uppercase tracking-wide text-foreground-muted">
                                        {getColumnFieldTag(
                                            {
                                                id: c.id,
                                                name: c.name,
                                                color: c.color,
                                                filterRules: c.filterRules,
                                                visible: c.visible,
                                            },
                                            customFields,
                                            t,
                                        )}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Card fields section */}
                    <div className="space-y-2">
                        <h5 className="text-xs font-medium text-foreground uppercase tracking-wide">
                            {t('kanban.cardFieldsSection')}
                        </h5>
                        <p className="text-[11px] text-foreground-muted">
                            {t('kanban.cardFieldsHint')}
                        </p>
                        <div className="space-y-1 max-h-56 overflow-y-auto rounded-md border border-border/40 bg-surface-hover/20 p-1">
                            {renderableFields.length === 0 && (
                                <p className="text-xs text-foreground-muted px-2 py-3">
                                    {t('kanban.noCardFieldsAvailable')}
                                </p>
                            )}
                            {renderableFields.map((field) => {
                                const checked = selectedFieldIds.includes(field.id);
                                return (
                                    <label
                                        key={field.id}
                                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer select-none"
                                    >
                                        <Checkbox
                                            checked={checked}
                                            disabled={disabled}
                                            onCheckedChange={() => onToggleCardField(field.id)}
                                        />
                                        <span className="text-sm truncate flex-1">{field.name}</span>
                                        <span className="text-[10px] uppercase tracking-wide text-foreground-muted">
                                            {field.type}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}